import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Eye, Pencil, Plus } from "lucide-react";
import { businessesApi } from "@/api/counterparties";
import { ApiError } from "@/api/client";
import type { BusinessListItemDto } from "@/types/counterparties";
import { useDateFormatter } from "@/utils/datetime";
import { COUNTERPARTY_PERMISSIONS } from "@/types/counterparties";
import { Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { PageHeader } from "@/components/layout/PageHeader";
import { Pagination } from "@/components/layout/Pagination";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { RowActions, type RowAction } from "@/components/ux";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { CounterpartyStatusBadge } from "@/components/counterparties/StatusBadge";
import { ActiveDealsBlockedDialog } from "@/components/counterparties/ActiveDealsBlockedDialog";
import { businessKeys, invalidateCounterpartyReads } from "../counterpartyKeys";

const PERMS = COUNTERPARTY_PERMISSIONS.Business;
const PAGE_SIZE = 25;

export function BusinessListPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const navigate = useNavigate();
  const { date } = useDateFormatter();

  const canCreate = hasPermission(PERMS.Create);
  const canUpdate = hasPermission(PERMS.Update);
  const canDelete = hasPermission(PERMS.Delete);

  const [search, setSearch] = useState("");
  const [archiveScope, setArchiveScope] = useState<"Active" | "All" | "Archived">("Active");
  const [blockedFor, setBlockedFor] = useState<BusinessListItemDto | null>(null);
  const [page, setPage] = useState(1);

  useBreadcrumbs([{ label: "Businesses" }]);

  // Reset to page 1 whenever the filter set changes so we don't land on a
  // page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [search, archiveScope]);

  const businessesQuery = useQuery({
    queryKey: businessKeys.list({ search, archiveScope, page }),
    queryFn: () =>
      businessesApi.list({
        q: search || undefined,
        includeArchived: archiveScope === "All",
        archivedOnly: archiveScope === "Archived",
        page,
        pageSize: PAGE_SIZE,
      }),
  });

  const totalPages = useMemo(() => {
    const data = businessesQuery.data;
    if (!data || data.pageSize <= 0) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [businessesQuery.data]);

  const archive = useMutation({
    mutationFn: (id: string) => businessesApi.archive(id),
    onSuccess: () => {
      invalidateCounterpartyReads(queryClient, "business");
      toast.success("Business archived.");
    },
    onError: (e, id) => {
      if (e instanceof ApiError && e.status === 409) {
        const row = (businessesQuery.data?.data ?? []).find((p) => p.id === id);
        if (row) setBlockedFor(row);
      } else {
        toast.error(e instanceof Error ? e.message : "Archive failed.");
      }
    },
  });

  const rows = businessesQuery.data?.data ?? [];

  const columns: Column<BusinessListItemDto>[] = [
    {
      key: "name",
      header: "Legal entity",
      render: (b) => (
        <Link to={`/counterparties/businesses/${b.id}`} className="font-semibold hover:text-gold-shadow">
          {b.legalEntityName}
          {b.dba && <span className="text-caption text-gray-mid font-normal"> · DBA {b.dba}</span>}
        </Link>
      ),
    },
    { key: "naics", header: "NAICS", render: (b) => b.naicsCode ?? "—" },
    {
      key: "hq",
      header: "HQ",
      render: (b) => (b.hqCity || b.hqState ? `${b.hqCity ?? "—"}, ${b.hqState ?? ""}` : "—"),
    },
    {
      key: "status",
      header: "Status",
      render: (b) => <CounterpartyStatusBadge status={b.status} />,
    },
    {
      key: "created",
      header: "Created",
      render: (b) => date(b.createdAt),
    },
    {
      key: "actions",
      header: "",
      className: "w-10 text-right",
      render: (b) => (
        <RowActions
          actions={[
            { key: "view", label: "View", icon: Eye, href: `/counterparties/businesses/${b.id}` },
            {
              key: "edit",
              label: "Edit",
              icon: Pencil,
              hidden: !canUpdate,
              href: `/counterparties/businesses/${b.id}/edit`,
            },
            {
              key: "archive",
              label: "Archive",
              icon: Archive,
              destructive: true,
              hidden: !canDelete || b.status === "Archived",
              onClick: async () => {
                const ok = await dialogs.confirm({
                  title: `Archive ${b.legalEntityName}?`,
                  message: "This business will no longer appear in active lists.",
                  confirmLabel: "Archive",
                  destructive: true,
                });
                if (!ok) return;
                archive.mutate(b.id);
              },
            },
          ] satisfies RowAction[]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Counterparties"
        title="Businesses"
        actions={
          canCreate && (
            <Button size="sm" onClick={() => navigate("/counterparties/businesses/new")}>
              <Plus size={14} strokeWidth={1.5} aria-hidden /> New business
            </Button>
          )
        }
      />

      <div className="flex items-center gap-space-3 mb-space-3">
        <Input
          type="search"
          placeholder="Search by name, DBA, NAICS, HQ city/state…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="grow"
          aria-label="Search businesses"
        />
        <Select
          value={archiveScope}
          onChange={(e) => setArchiveScope(e.target.value as typeof archiveScope)}
          className="max-w-[10rem] shrink-0"
          aria-label="Archive scope"
        >
          <option value="Active">Active</option>
          <option value="All">All</option>
          <option value="Archived">Archived</option>
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={businessesQuery.isLoading}
        error={businessesQuery.isError ? (businessesQuery.error as Error).message : null}
        emptyMessage={
          canCreate
            ? "No businesses match your filters. Click “New business” to add one."
            : "No businesses match your filters."
        }
        rowKey={(b) => b.id}
      />

      {businessesQuery.data && businessesQuery.data.totalCount > 0 && (
        <Pagination
          page={businessesQuery.data.page}
          pageSize={businessesQuery.data.pageSize}
          totalCount={businessesQuery.data.totalCount}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}

      {blockedFor && (
        <ActiveDealsBlockedDialog
          open
          displayName={blockedFor.legalEntityName}
          onClose={() => setBlockedFor(null)}
        />
      )}
    </>
  );
}
